import { useEffect, useState } from 'react';
import {
  View,
  Text,
  Dimensions,
  StyleSheet,
  Pressable,
  TouchableWithoutFeedback,
  Keyboard,
  Platform,
} from 'react-native';
import { FontAwesome6 } from '@expo/vector-icons';
import {
  getCurrentDate,
  getOneMonthBackDate,
} from '../common/utils/date-time-conversions';
import DateRangePicker, {
  DateRange,
} from '../../components/DateRangePicker.native';

const { width, height } = Dimensions.get('window');

const selectAReport = [
  {
    label: 'Last month',
    value: '1',
  },
  {
    label: 'Custom dates',
    value: '2',
  },
];

const DateFilterModal = (props: {
  selectedOption: any;
  setSelectedOption: (option: any) => void;
  filterDate: (setDates: any) => void;
  handleCancel: () => void;
  //handleCustomDatePicker: (date: string, pickerType: 'start' | 'end') => void,
  customDates: { start: string | null; end: string | null };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [customDate, setCustomDate] = useState<number>(0);
  const [storedDates, setStoredDates] = useState<{
    start: string;
    end: string;
  }>({ start: getOneMonthBackDate(), end: getCurrentDate() });
  const [editable, setEditable] = useState(true);
  const [dateRangeModalVisible, setDateRangeModalVisible] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const selectOption = (option: any, index: number) => {
    props?.setSelectedOption(option.label);

    setCustomDate(index);
    // Set defaults
    setStoredDates({
      start: getOneMonthBackDate(),
      end: getCurrentDate(),
    });
    setEditable(index === 0 ? false : true);
    setIsOpen(false);
  };

  useEffect(() => {
    setCustomDate(props.selectedOption === 'Custom dates' ? 1 : 0);
    storeDates('start', `${props.customDates?.start}`);
    storeDates('end', `${props.customDates?.end}`);
  }, [props.customDates]);

  const storeDates = (key: 'start' | 'end', date: string) => {
    setStoredDates((prevDates) => ({
      ...prevDates,
      [key]: date,
    }));
  };

  return (
    <Pressable
      onPress={() => {
        Keyboard.dismiss(); // Dismiss keyboard if open
        props.handleCancel(); // Dismiss the modal
      }}
      style={styles.centeredView}
    >
      <TouchableWithoutFeedback onPress={() => {}}>
        <View style={[styles.modalView, { zIndex: 1 }]}>
          <Text className="text-base font-bold text-center text-white">
            Filter settings
          </Text>
          <Text className="px-3 py-2 text-sm font-medium text-center text-white">
            Select a date range to view your charger performance:
          </Text>
          <View
            className="relative"
            style={{
              position: 'relative',
              alignItems: 'center',
              marginVertical: 12,
            }}
          >
            <Pressable
              onPress={toggleDropdown}
              style={[styles.dropdownTrigger, styles.BgColor3]}
              className="flex-row justify-between px-3 py-2 rounded-full"
            >
              <Text className="text-sm font-medium text-white">
                {props.selectedOption || 'Select dates'}
              </Text>
              <FontAwesome6
                name={isOpen ? 'chevron-up' : 'chevron-down'}
                size={16}
                color="white"
              />
            </Pressable>
            {isOpen && (
              <View
                style={[styles.dropdownContainer, { elevation: 6 }]}
                className="bg-zinc-100"
              >
                {selectAReport.map((option, index) => (
                  <Pressable
                    key={`reportSelect${index}`}
                    onPress={() => {
                      selectOption(option, index);
                    }}
                    style={{ padding: 10, zIndex: 30 }}
                  >
                    <Text>{option.label}</Text>
                  </Pressable>
                ))}
              </View>
            )}
            <View className="justify-center items-center mt-2 w-full h-20 -z-10">
              {customDate === 1 && (
                <Text className="text-sm font-medium text-white">
                  Select a start and end date:
                </Text>
              )}
              <View className="flex-row justify-between items-center mt-2 w-full h-10">
                {customDate === 0 ? (
                  <>
                    <View className="bg-[#636363] px-5 py-2 rounded-full">
                      <Text className="text-white rounded-full">
                        {getOneMonthBackDate()}
                      </Text>
                    </View>
                    <Text className="text-sm font-medium text-white">to</Text>
                    <View className="bg-[#636363] px-5 py-2 rounded-full">
                      <Text className="text-white rounded-full">
                        {getCurrentDate()}
                      </Text>
                    </View>
                  </>
                ) : (
                  <View
                    style={{
                      width: '100%',
                      alignItems: 'center',
                      marginTop: 8,
                      marginBottom: 8,
                    }}
                  >
                    <Pressable
                      onPress={() => setDateRangeModalVisible(true)}
                      className="bg-[#0B1E1D] px-5 py-2 h-10 justify-center items-center rounded-full"
                      style={{ alignSelf: 'center' }}
                    >
                      <Text
                        className="text-sm font-medium text-white"
                        style={{ textAlign: 'center' }}
                      >
                        {storedDates.start && storedDates.end
                          ? `${storedDates.start} to ${storedDates.end}`
                          : 'Select custom range'}
                      </Text>
                    </Pressable>
                    <DateRangePicker
                      visible={dateRangeModalVisible}
                      onClose={() => setDateRangeModalVisible(false)}
                      onConfirm={(range: DateRange) => {
                        storeDates('start', range.startDate || '');
                        storeDates('end', range.endDate || '');
                      }}
                      initialRange={{
                        startDate: storedDates.start,
                        endDate: storedDates.end,
                      }}
                    />
                  </View>
                )}
              </View>
            </View>
          </View>
          <View
            className="flex-row justify-between items-center px-2 py-1"
            style={{ margin: 15 }}
          >
            <Pressable
              onPress={props.handleCancel}
              className="rounded-full border border-white"
            >
              <Text className="px-4 py-2 text-xs font-semibold text-white rounded-full">
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                setCustomDate(1);
                props.filterDate(storedDates);
              }}
              className="bg-[#51B868] px-4 py-2 rounded-full"
            >
              <Text
                className="text-xs font-semibold text-white"
                style={styles.BgColor}
              >
                Save Settings
              </Text>
            </Pressable>
          </View>
        </View>
      </TouchableWithoutFeedback>
    </Pressable>
  );
};

export default DateFilterModal;

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  BgColor: {
    backgroundColor: '#51B868',
  },
  BgColor1: {
    backgroundColor: '#39BEB780',
  },
  BgColor2: {
    backgroundColor: '#F97777',
  },
  BgColor3: {
    backgroundColor: '#39BEB7',
  },
  BgColor4: {
    backgroundColor: '#636363',
  },
  TextColor1: {
    color: '#E8E8E9',
  },
  textColor3: {
    color: '#8D8E90',
  },
  line: {
    borderBottomColor: '#636363',
    borderBottomWidth: 1,
    borderStyle: 'dashed',
    marginVertical: 20,
  },
  filterIcon: {
    height: 17,
    width: 17,
    backgroundColor: '#39BEB7',
  },
  centeredView: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: -999,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalView: {
    margin: 20,
    backgroundColor: '#292D33',
    borderRadius: 30,
    padding: 10,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    width: 290,
    zIndex: 10,
  },
  dropdownTrigger: {
    width: width <= 430 ? width * 0.4 : 150,
  },
  dropdownContainer: {
    position: 'absolute',
    top: 30,
    backgroundColor: '#fff',
    borderRadius: 5,
    marginTop: 2,
    width: width <= 430 ? width * 0.4 : 150,
    elevation: 5,
  },
  alignEllipses: Platform.select({
    web: {
      fontWeight: '500',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: 150,
    },
    default: {
      fontWeight: '500',
      overflow: 'hidden',
      maxWidth: 150,
    },
  }),
  tooltipContainer: {
    borderRadius: 10,
    padding: 10,
    minWidth: 50,
    maxWidth: 500,
    width: 'auto',
    height: 'auto',
  },
});
